/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { configuration } from "@/lib/configuration";
import { EXTERNAL_APIS } from "@/lib/constants";
import * as Sentry from "@sentry/react";

export interface IPDetailsResponse {
  ip: string;
  /**
   * Set only when the lookup did not succeed. Absent on every real response.
   *
   * This used to be reported by putting a sentence in `ip` — "Timeout",
   * "Network error", "HTTP 429". Nothing downstream could tell that apart from
   * an address, so the My PC Info page rendered it as the visitor's IP at
   * display size, offered to copy it, and announced "Copied" when they did.
   * `ip` is now empty on failure and the reason is here, where a caller has to
   * look at it deliberately.
   *
   * The two language-detection callers read only `languages`, so neither is
   * affected either way.
   */
  error?: "timeout" | "network" | "http";
  org?: string;
  /** ASN from GeoLite2-ASN (15169 = Google). */
  asn?: number | null;
  city?: string;
  country_code?: string;
  country_name?: string;
  continent_code?: string;
  /** Readable continent name, e.g. "North America". */
  continent_name?: string;
  timezone?: string;
  /** null on a failed or partial lookup. C-2 made the failure branch return
   *  the same fifteen keys as the success branch rather than omitting them,
   *  and the backend's default dictionary carries `latitude: None` /
   *  `longitude: None`. Every consumer tests `!= null` for that reason (N-1);
   *  the type has to admit null or that guard reads as dead code. */
  latitude?: number | null;
  longitude?: number | null;
  /** The site locale for this IP, as one BCP 47 tag — "de", "pt-BR", or
   *  "en" when the country is not one the site has a locale for. Always a
   *  single tag; never a list. Resolved from _COUNTRY_LANG on the backend. */
  languages?: string;
  /** B4: State/region name from the server-side GeoIP lookup. Replaces the
   *  browser-side call to the public Nominatim service (blocked by CSP). */
  region?: string;
  /** B4: Postal/ZIP code from the server-side GeoIP lookup. */
  postal_code?: string;
  /** MaxMind confidence radius in km. */
  accuracy_radius?: number | null;
}

// DD-7: Module-level cache — first caller (languageInitPromise in main.tsx) populates
// the cache; subsequent callers (MyPCInfo) get the cached result without a second
// network request to the internal /api/geo/ endpoint — no third-party dependency.
let cachedResponse: IPDetailsResponse | null = null;

/** Resets the module-level cache. Intended for use in tests only. */
export const resetIPDetailsCache = (): void => {
  cachedResponse = null;
};

/**
 * IP geolocation fetch with a 5-second hard timeout.
 * If /api/geo/ is unreachable, the fetch is aborted and the caller falls back
 * to English — the UI never freezes waiting on geolocation.
 */
const TIMEOUT_MS = 5_000;

const IPDetails = async (): Promise<IPDetailsResponse> => {
  if (cachedResponse) return cachedResponse;

  const url = `${configuration.backendURL}${EXTERNAL_APIS.GEO_PATH}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);

    if (!response.ok) {
      Sentry.captureException(new Error(`Geo API error: ${response.status}`), {
        tags: { api_method: "GET (geo)" },
        contexts: { request: { url, method: "GET" } },
      });
      return { ip: "", error: "http" };
    }

    const data = (await response.json()) as IPDetailsResponse;
    cachedResponse = data;
    return data;
  } catch (error) {
    clearTimeout(timer);
    const isTimeout =
      error instanceof DOMException && error.name === "AbortError";
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: {
          api_method: "GET (geo)",
          is_timeout: String(isTimeout),
        },
        contexts: { request: { url, method: "GET" } },
      },
    );
    return { ip: "", error: isTimeout ? "timeout" : "network" };
  }
};

export default IPDetails;
